import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { CustomDialog } from '../../Component/Dialog';
import Variable from '../../CredentialStore/CredentialDialog/impl/Variable';
import { CREDENTIAL } from '../../CredentialStore/CredentialConstants';
import useCredentialSave from '../../CredentialStore/useCredentialSave';

/**
 * Self-contained credential creation modal for the agent-driven credential-setup flow. Reuses the
 * Credential Store's VARIABLE form (`Variable`) but is intentionally a deletable island: it does
 * not depend on the CredentialStorePage class component. v1 supports VARIABLE secrets only; the
 * placeholder is locked to the value the agent requested so the saved credential resolves the exact
 * ${placeholder.SECRET} token the function code references.
 */
export default function CredentialSetupModal({
  open,
  initialPlaceholder = '',
  suggestedDisplayName = '',
  suggestedDescription = '',
  lockPlaceholder = true,
  onClose,
  onSaved,
}) {
  const { saveCredential, saving } = useCredentialSave();

  const [credential, setCredential] = useState(() => ({
    name: suggestedDisplayName || '',
    description: suggestedDescription || '',
    placeholder: initialPlaceholder || '',
    secretValue: '',
    status: CREDENTIAL.CREDENTIAL_STATUS.DISCONNECTED,
  }));
  const [hasInteractedWithSecret, setHasInteractedWithSecret] = useState(false);
  const [isSecretValueValid, setIsSecretValueValid] = useState(true);
  const [error, setError] = useState(null);

  const handleChange = useCallback(
    (fieldKey, value) => {
      // Locked placeholder: the agent requested this exact key — ignore edits so the saved secret
      // resolves the ${placeholder.SECRET} token the code references.
      if (lockPlaceholder && fieldKey === CREDENTIAL.FIELD_KEYS.PLACEHOLDER) return;
      setCredential((prev) => ({ ...prev, [fieldKey]: value }));
    },
    [lockPlaceholder]
  );

  const secretMissing = !credential.secretValue || credential.secretValue.trim() === '';
  const canSave = !saving && !secretMissing && isSecretValueValid && Boolean(credential.placeholder);

  const handleSave = useCallback(async () => {
    setError(null);
    try {
      const result = await saveCredential({
        name: credential.name,
        description: credential.description,
        placeholder: credential.placeholder,
        secretValue: credential.secretValue,
      });
      if (typeof onSaved === 'function') {
        await onSaved(result);
      }
    } catch (e) {
      setError(e?.message || 'Failed to save credential');
    }
  }, [saveCredential, credential, onSaved]);

  const contents = useMemo(
    () => (
      <Box sx={{ pt: 0.5 }}>
        <Variable
          credential={credential}
          onChange={handleChange}
          credentialStatus={CREDENTIAL.INFO_DIALOGUE_STATUS.NEW}
          hasInteractedWithSecret={hasInteractedWithSecret}
          showPasswordField={true}
          onInteractedWithSecret={() => setHasInteractedWithSecret(true)}
          onShowPasswordField={() => {}}
          onSecretValueValidationChange={setIsSecretValueValid}
        />
        {error && (
          <Typography sx={{ mt: 1.5, fontSize: '13px', color: 'error.main', fontWeight: 600 }}>
            {error}
          </Typography>
        )}
      </Box>
    ),
    [credential, handleChange, hasInteractedWithSecret, error]
  );

  return (
    <CustomDialog
      isOpen={open}
      size="sm"
      title="Add secret"
      contents={contents}
      buttons={
        <>
          <Button
            size="small"
            variant="text"
            onClick={onClose}
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            Save secret
          </Button>
        </>
      }
    />
  );
}
